import React, { useState, useEffect, useRef, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Shared horizontal scroll row with desktop arrow buttons + optional grid
// expand toggle.
//
// Props:
//   children   — card/item nodes
//   expanded   — if true, renders as a responsive grid instead of a scroll row
//   cardSize   — minimum card width in px used by the expanded grid (default 160)
// ---------------------------------------------------------------------------
export default function HScrollRow({ children, expanded, cardSize = 160 }) {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 10);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    if (expanded) return;
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [update, expanded]);

  const scroll = (dir) => {
    ref.current?.scrollBy({ left: dir * 500, behavior: 'smooth' });
  };

  const arrowStyle = (side) => ({
    position: 'absolute', top: '50%', [side]: -18,
    transform: 'translateY(-70%)', zIndex: 5,
    width: 36, height: 36, borderRadius: '50%',
    background: '#fff', border: '1px solid #e0e0e0',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: '#333', fontSize: 18,
    transition: 'opacity 0.2s',
  });

  if (expanded) {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))`,
        gap: 16,
        transition: 'all 0.3s ease',
      }}>
        {children}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {canLeft && (
        <button onClick={() => scroll(-1)} style={arrowStyle('left')} className="hidden md:flex" aria-label="Scroll left">
          ‹
        </button>
      )}
      <div
        ref={ref}
        style={{
          display: 'flex', gap: 16, overflowX: 'auto', overflowY: 'hidden',
          paddingBottom: 8, scrollBehavior: 'smooth',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
        className="no-scrollbar"
      >
        {children}
      </div>
      {canRight && (
        <button onClick={() => scroll(1)} style={arrowStyle('right')} className="hidden md:flex" aria-label="Scroll right">
          ›
        </button>
      )}
    </div>
  );
}
